import { Params } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { MaintainService } from '../maintain.service';
import {
  ActionConfigDTO,
  ElasticConfigDTO,
  FieldConfigDTO,
  FormRowConfigDTO,
  MaintainTabDTO,
  MenuDTO,
  RowConditionDTO,
} from '../../../../system/system-dtos';
import { PageRequestDTO, PageResponseDTO } from '../../../../common/paging-dto';
import { KibanaQueryDTO, QueryConditionDTO, QueryOperand } from '../../../../common/kibana-request-dto';
import { ExtractedActions } from '../../../../common/event-dtos';
import { DataSource } from './data-source';
import { EncodingUtils } from '../../../../common/encoding-utils';
import { MenuUtils } from '../../../../common/utils/menu-utils';
import { CohortDO } from '../../../../common/domain-model-dtos';
import { AdminContextService } from '../../../../common/services/admin.context.service';
import { isDefined } from '../../../utils/utils';

@Injectable()
export class QueryDataSource extends DataSource {
  currentCohort: CohortDO;

  public constructor(
    private maintainService: MaintainService,
    adminContextService: AdminContextService
  ) {
    super();
    adminContextService.getCohortWatcher().subscribe((cohort: CohortDO) => {
      this.currentCohort = cohort;
    });
  }

  public buildKibanaQuery(
    menuConfig: MenuDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any,
    queryParams: Params,
    currentTab: MaintainTabDTO
  ): KibanaQueryDTO {
    const { currentMode } = menuConfig.entityGroup;
    const elasticConfig = currentMode
      ? MenuUtils.getNthConfigAsElastic(menuConfig.entityGroup, currentMode)
      : MenuUtils.getFirstElasticConfig(menuConfig.entityGroup);

    const kibanaQuery: KibanaQueryDTO = {
      index: elasticConfig.indexName,
      offset: pageRequest.offset,
      perPage: pageRequest.perPage,
      convertTo: elasticConfig.convertTo,
      indexClass: elasticConfig.indexClass,
      queryConditions: {
        index: elasticConfig.indexName,
        conditions: this.createConditions(filters, elasticConfig, id, context),
      },
      orderParamTieBreaker: (currentTab || elasticConfig.tabs[0]).tableView.sortTieBreaker,
      after: queryParams.after,
      before: queryParams.before,
      last: queryParams.last,
    };

    if (pageRequest.order && !pageRequest.order.includes('null')) {
      const orderStringSplit = pageRequest.order.split(',');
      kibanaQuery.orderParam = orderStringSplit[0];
      kibanaQuery.orderDirection = orderStringSplit[1];
    }
    return kibanaQuery;
  }

  private createConditions(
    filters: { [p: string]: FieldConfigDTO },
    elasticConfig: ElasticConfigDTO,
    entityId: number,
    context: any
  ): QueryConditionDTO[] {
    const initialFilters = this.doGetInitialFilters(elasticConfig.tabs[0].initialConditions, entityId, context);
    const arr: QueryConditionDTO[] = [];

    this.mapFiltersToQueryConditions(filters).forEach((e) => arr.push(e));
    this.mapFiltersToQueryConditions(initialFilters).forEach((e) => arr.push(e));
    if (elasticConfig.includeCohortCondition) {
      arr.push(this.createCohortQueryCondition());
    }
    return arr;
  }

  private mapFiltersToQueryConditions(filters: { [p: string]: FieldConfigDTO }): QueryConditionDTO[] {
    return Object.keys(filters)
      .map((k) => filters[k])
      .filter((mf) => mf.value || mf.value === 0)
      .map((e) => this.createQueryCondition(e));
  }

  private createQueryCondition(e: any): QueryConditionDTO {
    return {
      fieldName: EncodingUtils.camelToKebabCase(e.filterElasticName),
      operand: e.elasticSearchOperand ? e.elasticSearchOperand : QueryOperand.EQUALS,
      value: DataSource.getObjectIfJsonOrString(e.value),
    };
  }

  private createCohortQueryCondition(): QueryConditionDTO {
    return {
      fieldName: 'cohort-id',
      operand: QueryOperand.EQUALS,
      value: isDefined(this.currentCohort) ? this.currentCohort.id : undefined,
    };
  }

  doGetInitialFilters(initialConditions: RowConditionDTO[], entityId: number, context: any) {
    const filters = {};
    if (!initialConditions || initialConditions.length === 0) {
      return filters;
    }

    initialConditions.forEach((condition) => {
      filters[condition.field] = {
        value: this.convertValue(condition, entityId, context),
        filterElasticName: EncodingUtils.camelToKebabCase(condition.field),
        elasticSearchOperand: condition.operand,
      };
    });

    return filters;
  }

  convertValue(condition, entityId: number, context: any) {
    const entity = context;
    const header = context;
    return eval(condition.value.replace('{entityId}', entityId));
  }

  public getTabs(menuConfig: MenuDTO): MaintainTabDTO[] {
    return MenuUtils.getFirstElasticConfig(menuConfig.entityGroup).tabs;
  }

  public getRows(menuConfig: MenuDTO): FormRowConfigDTO[] {
    return MenuUtils.getFirstElasticConfig(menuConfig.entityGroup).rows;
  }

  public doGetData(
    menuConfig: MenuDTO,
    currentTab: MaintainTabDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any = {},
    queryParams: Params
  ): Observable<PageResponseDTO<any>> {
    const kibanaQuery = this.buildKibanaQuery(menuConfig, pageRequest, filters, id, context, queryParams, currentTab);

    const timerStart = performance.now();
    const kibanaUrl =
      currentTab.endpoints && currentTab.endpoints.selectAllUrl
        ? currentTab.endpoints.selectAllUrl
        : MaintainService.KIBANA_QUERY_URL;
    return this.maintainService
      .performKibanaQueryWithUrl(kibanaQuery, kibanaUrl)
      .pipe(tap((d) => (d.executionMillis = ((performance.now() - timerStart) / 1000).toFixed(2))));
  }

  getTitle(menuConfig: MenuDTO): string {
    return menuConfig.label;
  }

  extractActions(menuConfig: MenuDTO, tab: MaintainTabDTO, element: any): ExtractedActions {
    return {
      rest: MenuUtils.getFirstElasticConfig(menuConfig.entityGroup).restActions,
      simple: [],
    };
  }

  getTabsWithCount(menuConfig: MenuDTO): Observable<{ [p: string]: number }> {
    return new Observable<{ [p: string]: number }>((observer) => {
      observer.next(null);
      observer.complete();
    });
  }

  override getNoteForData(menu: MenuDTO): string {
    return 'Bear in mind that this list is not real time. Changes might take up to 10 secs to be reflected  after actual update. Refresh button is your friend.';
  }

  getOverallRestActions(menu: MenuDTO): ActionConfigDTO[] {
    return MenuUtils.getFirstElasticConfig(menu.entityGroup).overallRestActions;
  }
}
